import {useMemo} from 'react';
import Typography from '@mui/material/Typography';
import {styled} from '@mui/material/styles';
import {DialogActions, useTheme} from '@mui/material';
import Button from '@mui/material/Button';

import {FilterDialogSection} from 'filters/features/FilterDialog/types';

import {useIsDownSm} from 'utils/hooks/useMediaQuery';
import {useEditedFilterContext} from 'filters/features/FilterDialog/contexts/internal/EditedFilterProvider/EditedFilterProvider';
import {useValidationContext} from 'filters/features/FilterDialog/contexts/internal/ValidationProvider/ValidationProvider';
import {useFilterActionsContext} from 'filters/contexts/FilterActionsProvider/FilterActionsProvider';

import InfoIcon from 'components/icons/info.svg';
import IconWarningTriangle from 'components/icons/warningTriangle.svg';

import {PrimaryActionButton, SecondaryActionButton} from 'modules/decisionsNext/components/DecisionsDialog';
import {isEqualFilters} from 'modules/decisionsNext/components/BaseFilter/utils/filter';

const PrimaryAction = ({section}: {section?: FilterDialogSection}) => {
  const {isDirty} = useEditedFilterContext();

  if (section && 'footer' in section && section.footer?.primaryAction) {
    const PrimaryAction = section.footer.primaryAction;
    const {footer: _, ...rest} = section;
    return <PrimaryAction {...(rest as any)} />;
  }

  return (
    <PrimaryActionButton disabled={!isDirty} type='submit' data-testid='filter-dialog-apply-button'>
      Apply filters
    </PrimaryActionButton>
  );
};

const SecondaryAction = ({section, onClose}: {section?: FilterDialogSection; onClose?: () => void}) => {
  if (section && 'footer' in section && section.footer?.secondaryAction) {
    const SecondaryAction = section.footer.secondaryAction;
    const {footer: _, ...rest} = section;

    return <SecondaryAction {...(rest as any)} />;
  }

  return <SecondaryActionButton onClick={onClose}>Cancel</SecondaryActionButton>;
};

const useAdditionalActions = (section?: FilterDialogSection) => {
  const {useStoredFilters} = useFilterActionsContext();
  const isDownSm = useIsDownSm();
  const {filter, setFilter} = useEditedFilterContext();
  const {
    savedFilters: {defaultFilter},
  } = useStoredFilters();

  const isDefault = useMemo(
    () => (defaultFilter ? isEqualFilters(filter, defaultFilter) : false),
    [defaultFilter, filter]
  );

  if (section && 'footer' in section && section.footer?.additionalActions) {
    return section.footer.additionalActions(section as any);
  }

  if (!defaultFilter) {
    return [];
  }

  return [
    <Button
      color='inherit'
      variant='text'
      fullWidth={isDownSm}
      size='medium'
      key='reset'
      disabled={isDefault}
      onClick={() => setFilter(defaultFilter)}
      data-testid='filter-dialog-reset-button'
    >
      Reset to default
    </Button>,
  ];
};

const SErrorMessage = styled('div')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),

  [theme.breakpoints.down('sm')]: {
    alignSelf: 'baseline',
  },
}));

const ValidationMessage = () => {
  const theme = useTheme();
  const {fieldErrors, formError, formWarning} = useValidationContext();

  const withFieldErrors = Object.keys(fieldErrors).length > 0;

  if (!withFieldErrors && !formError && !formWarning) {
    return null;
  }

  return (
    <SErrorMessage>
      {withFieldErrors || formError ? (
        <InfoIcon color={theme.palette.error.main} width={20} />
      ) : (
        <IconWarningTriangle color={theme.palette.error.main} width={20} />
      )}

      <Typography variant='body3' color='error.main'>
        {withFieldErrors ? 'Invalid filters' : formError || formWarning}
      </Typography>
    </SErrorMessage>
  );
};

const SDialogActions = styled(DialogActions)(({theme}) => ({
  '&&': {
    flexDirection: 'column',
    gap: theme.spacing(2),

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
}));

const SRow = styled('div')(({theme}) => ({
  width: '100%',
  display: 'flex',
  gap: theme.spacing(2),
}));

const SDivider = styled('div')(() => ({
  flex: 1,
}));

export const FilterDialogActions = ({section, onClose}: {section?: FilterDialogSection; onClose: () => void}) => {
  const isDownSm = useIsDownSm();

  const primaryAction = <PrimaryAction section={section} />;
  const secondaryAction = <SecondaryAction section={section} onClose={onClose} />;
  const additionalActions = useAdditionalActions(section);
  const errorMessage = <ValidationMessage />;

  if (isDownSm) {
    return (
      <SDialogActions>
        {errorMessage}
        <SRow>
          {secondaryAction}
          {primaryAction}
        </SRow>
        {additionalActions}
      </SDialogActions>
    );
  }

  return (
    <SDialogActions>
      {errorMessage}
      <SDivider />
      {additionalActions}
      {secondaryAction}
      {primaryAction}
    </SDialogActions>
  );
};
